import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  TextField,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Autocomplete,
  MenuItem,
  Collapse,
  Checkbox,
  Divider,
  useTheme,
  InputAdornment,
  TablePagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Send as SubmitIcon,
  ShoppingCart as ShoppingCartIcon,
  TrendingUp as TrendingUpIcon,
  LocalShipping as FulfilledIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PriceChange as PriceChangeIcon,
  MergeType as ConsolidateIcon,
  Receipt as InvoiceIcon,
  DeleteOutline as RemoveLineIcon,
  Undo as UndoIcon,
  Print as PrintIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { useLanguage } from '../contexts/LanguageContext';
import { useConfirm } from '../hooks/useConfirm';
import { useFiscalDateRange } from '../hooks/useFiscalDateRange';
import { branchesService } from '../services/branches.service';
import { custodiesService } from '../services/custodies.service';
import { itemsService, itemUnitsService } from '../services/items.service';
import branchPurchaseRequestsService from '../services/branchPurchaseRequests.service';
import axiosInstance from '../services/axiosInstance';
import {
  BranchPurchaseRequest,
  BranchPurchaseRequestDetail,
  ConsolidatedRequests,
  ConsolidatedRequestItem,
  DailyItemPrice,
} from '../types/branchPurchaseRequests.types';
import { Branch } from '../types/branches.types';
import { Custody } from '../types/custodies.types';
import { Item, ItemUnit } from '../types/items.types';

// ---------------------------------------------------------------------------
// Translations
// ---------------------------------------------------------------------------
const translations = {
  en: {
    title: 'Branch Purchase Requests',
    myRequests: 'My Requests',
    consolidatedView: 'Shopping List',
    dailyPricing: 'Daily Pricing',
    newRequest: 'New Request',
    editRequest: 'Edit Request',
    requestNumber: 'Request #',
    branch: 'Branch',
    custody: 'Custody',
    date: 'Date',
    requiredDate: 'Required Date',
    status: 'Status',
    itemsCount: 'Items',
    actions: 'Actions',
    search: 'Search requests...',
    noData: 'No purchase requests found',
    draft: 'Draft',
    submitted: 'Submitted',
    approved: 'Approved',
    rejected: 'Rejected',
    fulfilled: 'Fulfilled',
    cancelled: 'Cancelled',
    all: 'All',
    totalRequests: 'Total Requests',
    pendingApproval: 'Pending Approval',
    approvedCount: 'Approved',
    fulfilledCount: 'Fulfilled',
    edit: 'Edit',
    delete: 'Delete',
    submit: 'Submit',
    approve: 'Approve',
    reject: 'Reject',
    view: 'View',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    notes: 'Notes',
    item: 'Item',
    unit: 'Unit',
    quantity: 'Quantity',
    priority: 'Priority',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    addLine: 'Add Item',
    removeLine: 'Remove',
    selectBranch: 'Select Branch',
    selectCustody: 'Select Custody',
    selectItem: 'Select Item',
    selectUnit: 'Select Unit',
    confirmDelete: 'Are you sure you want to delete this request?',
    confirmSubmit: 'Submit this request for approval?',
    confirmApprove: 'Approve this purchase request?',
    rejectReason: 'Rejection Reason',
    rejectReasonPlaceholder: 'Enter the reason for rejection...',
    successCreate: 'Purchase request created successfully.',
    successUpdate: 'Purchase request updated successfully.',
    successDelete: 'Purchase request deleted successfully.',
    successSubmit: 'Purchase request submitted successfully.',
    successApprove: 'Purchase request approved successfully.',
    successReject: 'Purchase request rejected successfully.',
    errorAction: 'An error occurred. Please try again.',
    errorLoadData: 'Failed to load data.',
    refresh: 'Refresh',
    filterByBranch: 'Filter by Branch',
    filterByCustody: 'Filter by Custody',
    filterByStatus: 'Filter by Status',
    fromDate: 'From Date',
    toDate: 'To Date',
    // Consolidated tab
    totalBranches: 'Total Branches',
    totalItems: 'Total Items',
    totalQty: 'Total Qty',
    branchBreakdown: 'Branch Breakdown',
    convertToOrders: 'Convert to Purchase Orders',
    convertToOrdersConfirm: 'This will create a separate purchase order for each approved request. Continue?',
    successConvertOrders: 'Successfully converted to purchase orders.',
    requestedBy: 'Requested By',
    approvedBy: 'Approved By',
    approvedQty: 'Approved Qty',
    requestedQty: 'Requested Qty',
    // Pricing tab
    pricingDate: 'Pricing Date',
    unitPrice: 'Unit Price',
    applyPricing: 'Apply Pricing',
    convertToInvoices: 'Convert to Invoices',
    successPricing: 'Pricing applied successfully.',
    successConvertInvoices: 'Successfully converted to invoices.',
    convertToInvoicesConfirm: 'Convert priced orders to purchase invoices?',
    noPricingData: 'No pricing data for this date.',
    currentPrice: 'Current Price',
    lineNumber: '#',
    rejectionReason: 'Rejection Reason',
    requestDetails: 'Request Details',
    // Surplus Stock tab
    surplusStock: 'Surplus Stock',
    surplusDescription: 'Enter extra quantities to store in a main warehouse',
    selectWarehouse: 'Select Warehouse',
    addSurplusItem: 'Add Item',
    createSurplusInvoice: 'Create Invoice',
    surplusInvoiceCreated: 'Surplus invoice created successfully',
    total: 'Total',
  },
  ar: {
    title: 'طلبات الشراء الفرعية',
    myRequests: 'طلباتي',
    consolidatedView: 'قائمة التسوّق',
    dailyPricing: 'التسعير اليومي',
    newRequest: 'طلب جديد',
    editRequest: 'تعديل الطلب',
    requestNumber: 'رقم الطلب',
    branch: 'الفرع',
    custody: 'العهدة',
    date: 'التاريخ',
    requiredDate: 'التاريخ المطلوب',
    status: 'الحالة',
    itemsCount: 'الأصناف',
    actions: 'الإجراءات',
    search: 'البحث في الطلبات...',
    noData: 'لا توجد طلبات شراء',
    draft: 'مسودة',
    submitted: 'مقدّم',
    approved: 'معتمد',
    rejected: 'مرفوض',
    fulfilled: 'منفّذ',
    cancelled: 'ملغي',
    all: 'الكل',
    totalRequests: 'إجمالي الطلبات',
    pendingApproval: 'بانتظار الاعتماد',
    approvedCount: 'معتمد',
    fulfilledCount: 'منفّذ',
    edit: 'تعديل',
    delete: 'حذف',
    submit: 'تقديم',
    approve: 'اعتماد',
    reject: 'رفض',
    view: 'عرض',
    save: 'حفظ',
    cancel: 'إلغاء',
    close: 'إغلاق',
    notes: 'ملاحظات',
    item: 'الصنف',
    unit: 'الوحدة',
    quantity: 'الكمية',
    priority: 'الأولوية',
    high: 'عالية',
    medium: 'متوسطة',
    low: 'منخفضة',
    addLine: 'إضافة صنف',
    removeLine: 'حذف',
    selectBranch: 'اختر الفرع',
    selectCustody: 'اختر العهدة',
    selectItem: 'اختر الصنف',
    selectUnit: 'اختر الوحدة',
    confirmDelete: 'هل أنت متأكد من حذف هذا الطلب؟',
    confirmSubmit: 'تقديم هذا الطلب للاعتماد؟',
    confirmApprove: 'اعتماد طلب الشراء هذا؟',
    rejectReason: 'سبب الرفض',
    rejectReasonPlaceholder: 'أدخل سبب الرفض...',
    successCreate: 'تم إنشاء طلب الشراء بنجاح.',
    successUpdate: 'تم تحديث طلب الشراء بنجاح.',
    successDelete: 'تم حذف طلب الشراء بنجاح.',
    successSubmit: 'تم تقديم طلب الشراء بنجاح.',
    successApprove: 'تم اعتماد طلب الشراء بنجاح.',
    successReject: 'تم رفض طلب الشراء بنجاح.',
    errorAction: 'حدث خطأ. يرجى المحاولة مرة أخرى.',
    errorLoadData: 'فشل في تحميل البيانات.',
    refresh: 'تحديث',
    filterByBranch: 'تصفية حسب الفرع',
    filterByCustody: 'تصفية حسب العهدة',
    filterByStatus: 'تصفية حسب الحالة',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    // Consolidated tab
    totalBranches: 'إجمالي الفروع',
    totalItems: 'إجمالي الأصناف',
    totalQty: 'إجمالي الكمية',
    branchBreakdown: 'تفاصيل الفروع',
    convertToOrders: 'تحويل إلى أوامر شراء',
    convertToOrdersConfirm: 'سيتم إنشاء أمر شراء منفصل لكل طلب معتمد. متابعة؟',
    successConvertOrders: 'تم التحويل إلى أوامر شراء بنجاح.',
    requestedBy: 'مقدم الطلب',
    approvedBy: 'معتمد من',
    approvedQty: 'الكمية المعتمدة',
    requestedQty: 'الكمية المطلوبة',
    // Pricing tab
    pricingDate: 'تاريخ التسعير',
    unitPrice: 'سعر الوحدة',
    applyPricing: 'تطبيق التسعير',
    convertToInvoices: 'تحويل إلى فواتير',
    successPricing: 'تم تطبيق التسعير بنجاح.',
    successConvertInvoices: 'تم التحويل إلى فواتير بنجاح.',
    convertToInvoicesConfirm: 'تحويل الأوامر المسعّرة إلى فواتير شراء؟',
    noPricingData: 'لا توجد بيانات تسعير لهذا التاريخ.',
    currentPrice: 'السعر الحالي',
    lineNumber: '#',
    rejectionReason: 'سبب الرفض',
    requestDetails: 'تفاصيل الطلب',
    // Surplus Stock tab
    surplusStock: 'المخزون الزائد',
    surplusDescription: 'أدخل الكميات الزائدة لتخزينها في المخزن الرئيسي',
    selectWarehouse: 'اختر المخزن',
    addSurplusItem: 'إضافة صنف',
    createSurplusInvoice: 'إنشاء فاتورة',
    surplusInvoiceCreated: 'تم إنشاء فاتورة المخزون الزائد بنجاح',
    total: 'الإجمالي',
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
interface DetailLine {
  key: number;
  itemId: number | null;
  unitId: number | null;
  requestedQuantity: number;
  priority: string;
  notes: string;
  availableUnits: ItemUnit[];
}

const emptyLine = (key: number): DetailLine => ({
  key,
  itemId: null,
  unitId: null,
  requestedQuantity: 0,
  priority: 'Medium',
  notes: '',
  availableUnits: [],
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
const BranchPurchaseRequestsPage2: React.FC = () => {
  const { language } = useLanguage();
  const theme = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();
  const { minDate, maxDate } = useFiscalDateRange();

  // ---- User context from JWT ----
  const { userBranchIds, userId } = useMemo(() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return { userBranchIds: [] as number[], userId: 0 };
      const payload = JSON.parse(atob(token.split('.')[1]));
      const primaryBranch = payload.BranchId ? [Number(payload.BranchId)] : [];
      const allowedBranches = payload.AllowedBranchId
        ? Array.isArray(payload.AllowedBranchId)
          ? payload.AllowedBranchId.map(Number)
          : [Number(payload.AllowedBranchId)]
        : [];
      const branchIds = Array.from(new Set([...primaryBranch, ...allowedBranches]));
      const uid = payload.UserId ? Number(payload.UserId) : 0;
      return { userBranchIds: branchIds, userId: uid };
    } catch {
      return { userBranchIds: [] as number[], userId: 0 };
    }
  }, []);

  const userPermissions = (() => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return [] as string[];
      const payload = JSON.parse(atob(token.split('.')[1]));
      const perms = payload.Permission;
      return perms ? (Array.isArray(perms) ? perms : [perms]) : [];
    } catch { return [] as string[]; }
  })();
  const isCentralBuyer = userPermissions.includes('CENTRAL_PURCHASING_PRICING') || userPermissions.includes('BRANCH_PURCHASE_REQUEST_APPROVE');

  // ---- State ----
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requests, setRequests] = useState<BranchPurchaseRequest[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [custodies, setCustodies] = useState<Custody[]>([]);
  const [items, setItems] = useState<Item[]>([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBranchId, setFilterBranchId] = useState<number | ''>('');
  const [filterCustodyId, setFilterCustodyId] = useState<number | ''>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Create/Edit Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRequest, setEditingRequest] = useState<BranchPurchaseRequest | null>(null);
  const [dialogBranchId, setDialogBranchId] = useState<number | ''>('');
  const [dialogCustodyId, setDialogCustodyId] = useState<number | ''>('');
  const [dialogRequiredDate, setDialogRequiredDate] = useState('');
  const [dialogNotes, setDialogNotes] = useState('');
  const [dialogLines, setDialogLines] = useState<DetailLine[]>([emptyLine(1)]);
  const [dialogSaving, setDialogSaving] = useState(false);
  const [lineKeyCounter, setLineKeyCounter] = useState(2);

  // View Dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewRequest, setViewRequest] = useState<BranchPurchaseRequest | null>(null);

  // Reject Dialog
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRequestId, setRejectRequestId] = useState<number | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Consolidated Tab
  const [consolidated, setConsolidated] = useState<ConsolidatedRequests | null>(null);
  const [consolidatedLoading, setConsolidatedLoading] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [selectedRequestIds, setSelectedRequestIds] = useState<Set<number>>(new Set());

  // Surplus Stock Tab
  const [surplusWarehouseId, setSurplusWarehouseId] = useState<number | ''>('');
  const [surplusLines, setSurplusLines] = useState<Array<{
    id: string;
    itemId: number;
    unitId: number;
    quantity: number;
    unitPrice: number;
    notes: string;
    availableUnits: ItemUnit[];
  }>>([]);
  const [mainWarehouses, setMainWarehouses] = useState<any[]>([]);
  const [poQuantities, setPoQuantities] = useState<Record<number, number>>({});
  // ✅ NEW: date-keyed price map { "itemId_yyyy-MM-dd": price } so each request looks up its OWN day price
  const [pricesByDate, setPricesByDate] = useState<Record<string, number>>({});
  const [surplusCreating, setSurplusCreating] = useState(false);
  const [surplusLineKeyCounter, setSurplusLineKeyCounter] = useState(1);
  // Single item pricing
  const [singlePriceItemId, setSinglePriceItemId] = useState<number | null>(null);
  const [singlePriceFrom, setSinglePriceFrom] = useState<string>(new Date().toISOString().split('T')[0]);
  const [singlePriceTo, setSinglePriceTo] = useState<string>(new Date().toISOString().split('T')[0]);
  const [singlePriceValue, setSinglePriceValue] = useState<number>(0);

  const [surplusFromDate, setSurplusFromDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [surplusToDate, setSurplusToDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [surplusInvoices, setSurplusInvoices] = useState<any[]>([]);
  const [surplusInvoicesLoading, setSurplusInvoicesLoading] = useState(false);

  // Daily Pricing Tab
  const [pricingFromDate, setPricingFromDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [pricingToDate, setPricingToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [dailyPrices, setDailyPrices] = useState<DailyItemPrice[]>([]);
  const [priceEdits, setPriceEdits] = useState<Record<number, number>>({});
  const [pricingLoading, setPricingLoading] = useState(false);

  // ---- Load reference data ----
  const loadReferenceData = useCallback(async () => {
    try {
      const [branchData, custodyData, itemData] = await Promise.all([
        branchesService.getAll(),
        custodiesService.getCentral(),
        itemsService.getAll(),
      ]);
      setBranches(branchData);
      setCustodies(custodyData);
      setItems(itemData);
      // Auto-select first central custody for consolidated/pricing tabs
      if (custodyData.length > 0 && !filterCustodyId) {
        setFilterCustodyId(custodyData[0].custodyId);
      }
      // Load main warehouses (no branchId) for Surplus Stock tab
      try {
        const warehouseData = await axiosInstance.get('/BranchPurchaseRequests/main-warehouses').then(r => r.data);
        setMainWarehouses(warehouseData);
      } catch { /* ignore warehouse load failure */ }
    } catch {
      toast.error(t.errorLoadData);
    }
  }, []);

  // ---- Load requests ----
  const loadRequests = useCallback(async () => {
    setLoading(true);
    try {
      const brId = filterBranchId || undefined;
      const cuId = filterCustodyId || undefined;
      const st = filterStatus || undefined;
      const data = await branchPurchaseRequestsService.getAll(
        brId as number | undefined,
        cuId as number | undefined,
        st,
      );
      const sorted = [...data].sort((a, b) => b.requestId - a.requestId);
      setRequests(sorted);
    } catch {
      toast.error(t.errorLoadData);
    } finally {
      setLoading(false);
    }
  }, [filterBranchId, filterCustodyId, filterStatus]);

  useEffect(() => {
    loadReferenceData();
  }, [loadReferenceData]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // ✅ FIX: Load ALL prices for the custody and build a date-keyed map
  // so each request in My Requests gets its OWN day's price (not last-loaded prices).
  useEffect(() => {
    if (filterCustodyId) {
      branchPurchaseRequestsService.getAllDailyPrices(filterCustodyId as number).then((data) => {
        const map: Record<string, number> = {};
        data.forEach((dp) => {
          // expand FromDate..ToDate into per-day keys
          const fromD = new Date(dp.fromDate);
          const toD = new Date(dp.toDate);
          for (let d = new Date(fromD); d <= toD; d.setDate(d.getDate() + 1)) {
            const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const key = `${dp.itemId}_${dateStr}`;
            // Prefer the most-specific or non-zero value if duplicates exist
            if (map[key] == null || (dp.unitPrice > 0 && map[key] === 0)) {
              map[key] = dp.unitPrice;
            }
          }
        });
        setPricesByDate(map);
      }).catch(() => setPricesByDate({}));
    } else {
      setPricesByDate({});
    }
  }, [filterCustodyId]);

  // ---- Filtered requests for table ----
  const filteredRequests = useMemo(() => {
    let list = requests;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (r) =>
          r.requestNumber.toLowerCase().includes(term) ||
          r.branchName?.toLowerCase().includes(term) ||
          r.branchNameAr?.toLowerCase().includes(term) ||
          r.custodyName?.toLowerCase().includes(term),
      );
    }
    if (filterDateFrom) {
      list = list.filter((r) => (r.requiredDate || r.requestDate)?.split('T')[0] >= filterDateFrom);
    }
    if (filterDateTo) {
      list = list.filter((r) => (r.requiredDate || r.requestDate)?.split('T')[0] <= filterDateTo);
    }
    return list;
  }, [requests, searchTerm, filterDateFrom, filterDateTo]);

  useEffect(() => { setPage(0); }, [searchTerm, filterDateFrom, filterDateTo, activeTab, filterBranchId, filterCustodyId, filterStatus]);

  const paginatedRequests = useMemo(
    () => filteredRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredRequests, page, rowsPerPage]
  );

  // ---- Stats ----
  const stats = useMemo(() => {
    const total = requests.length;
    const pending = requests.filter((r) => r.status === 'Submitted').length;
    const approved = requests.filter((r) => r.status === 'Approved').length;
    const fulfilled = requests.filter((r) => r.status === 'Fulfilled').length;
    return { total, pending, approved, fulfilled };
  }, [requests]);

  // ---- Formatting helpers ----
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getStatusColor = (status: string): 'default' | 'warning' | 'info' | 'success' | 'error' => {
    switch (status) {
      case 'Draft': return 'default';
      case 'Submitted': return 'warning';
      case 'Approved': return 'info';
      case 'Rejected': return 'error';
      case 'Fulfilled': return 'success';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'Draft': return t.draft;
      case 'Submitted': return t.submitted;
      case 'Approved': return t.approved;
      case 'Rejected': return t.rejected;
      case 'Fulfilled': return t.fulfilled;
      case 'Cancelled': return t.cancelled;
      default: return status;
    }
  };

  const getPriorityLabel = (p: string) => {
    switch (p) {
      case 'High': return t.high;
      case 'Medium': return t.medium;
      case 'Low': return t.low;
      default: return p;
    }
  };

  // ---- Dialog handlers ----
  const openCreateDialog = () => {
    setEditingRequest(null);
    setDialogBranchId(userBranchIds.length === 1 ? userBranchIds[0] : '');
    setDialogCustodyId('');
    setDialogRequiredDate('');
    setDialogNotes('');
    setDialogLines([emptyLine(1)]);
    setLineKeyCounter(2);
    setDialogOpen(true);
  };

  const openEditDialog = async (req: BranchPurchaseRequest) => {
    try {
      const full = await branchPurchaseRequestsService.getById(req.requestId);
      setEditingRequest(full);
      setDialogBranchId(full.branchId);
      setDialogCustodyId(full.custodyId || '');
      setDialogRequiredDate(full.requiredDate ? full.requiredDate.split('T')[0] : '');
      setDialogNotes(full.notes || '');

      let keyC = 1;
      const lines: DetailLine[] = await Promise.all(
        full.details.map(async (d) => {
          let units: ItemUnit[] = [];
          try {
            units = await itemUnitsService.getItemUnits(d.itemId);
          } catch { /* ignore */ }
          const k = keyC++;
          return {
            key: k,
            itemId: d.itemId,
            unitId: d.unitId,
            requestedQuantity: d.requestedQuantity,
            priority: d.priority || 'Medium',
            notes: d.notes || '',
            availableUnits: units,
          };
        }),
      );
      if (lines.length === 0) lines.push(emptyLine(keyC++));
      setDialogLines(lines);
      setLineKeyCounter(keyC);
      setDialogOpen(true);
    } catch {
      toast.error(t.errorAction);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingRequest(null);
  };

  const handleAddLine = () => {
    setDialogLines((prev) => [...prev, emptyLine(lineKeyCounter)]);
    setLineKeyCounter((c) => c + 1);
  };

  const handleRemoveLine = (key: number) => {
    setDialogLines((prev) => {
      const next = prev.filter((l) => l.key !== key);
      return next.length === 0 ? [emptyLine(lineKeyCounter)] : next;
    });
    if (dialogLines.length <= 1) setLineKeyCounter((c) => c + 1);
  };

  const handleLineItemChange = async (key: number, itemId: number | null) => {
    let units: ItemUnit[] = [];
    if (itemId) {
      try {
        units = await itemUnitsService.getItemUnits(itemId);
      } catch { /* ignore */ }
    }
    setDialogLines((prev) =>
      prev.map((l) =>
        l.key === key
          ? { ...l, itemId, unitId: units.length > 0 ? units[0].unitId : null, availableUnits: units }
          : l,
      ),
    );
  };

  const handleLineChange = (key: number, field: keyof DetailLine, value: unknown) => {
    setDialogLines((prev) =>
      prev.map((l) => (l.key === key ? { ...l, [field]: value } : l)),
    );
  };

  const handleSaveRequest = async () => {
    if (!dialogBranchId) {
      toast.error(isRTL ? 'يرجى اختيار الفرع' : 'Please select a branch');
      return;
    }
    const validLines = dialogLines.filter((l) => l.itemId && l.unitId && l.requestedQuantity > 0);
    if (validLines.length === 0) {
      toast.error(isRTL ? 'يرجى إضافة صنف واحد على الأقل' : 'Please add at least one item');
      return;
    }

    const dto: any = {
      branchId: dialogBranchId,
      custodyId: dialogCustodyId || null,
      requiredDate: dialogRequiredDate || null,
      // ✅ FIX: preserve original RequestDate when editing — only use today for new requests
      requestDate: editingRequest ? editingRequest.requestDate : new Date().toISOString(),
      notes: dialogNotes || null,
      details: validLines.map((l, idx) => ({
        lineNumber: idx + 1,
        itemId: l.itemId,
        unitId: l.unitId,
        requestedQuantity: l.requestedQuantity,
        priority: l.priority,
        notes: l.notes || null,
      })),
    };
    if (editingRequest) {
      dto.requestId = editingRequest.requestId;
    }

    setDialogSaving(true);
    try {
      if (editingRequest) {
        await branchPurchaseRequestsService.update(editingRequest.requestId, dto);
        toast.success(t.successUpdate);
      } else {
        await branchPurchaseRequestsService.create(dto);
        toast.success(t.successCreate);
      }
      handleDialogClose();
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    } finally {
      setDialogSaving(false);
    }
  };

  // ---- Row actions ----
  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try {
      await branchPurchaseRequestsService.delete(id);
      toast.success(t.successDelete);
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  const handleSubmit = async (id: number) => {
    const ok = await confirm({ message: t.confirmSubmit, severity: 'warning' });
    if (!ok) return;
    try {
      await branchPurchaseRequestsService.submit(id);
      toast.success(t.successSubmit);
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  const handleApprove = async (id: number) => {
    const ok = await confirm({ message: t.confirmApprove, severity: 'warning' });
    if (!ok) return;
    try {
      await branchPurchaseRequestsService.approve(id);
      toast.success(t.successApprove);
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  const handleRecall = async (id: number) => {
    const ok = await confirm({ message: isRTL ? '\u0625\u0644\u063a\u0627\u0621 \u0627\u0644\u062a\u0642\u062f\u064a\u0645\u061f' : 'Recall this request?', severity: 'warning' });
    if (!ok) return;
    try {
      await branchPurchaseRequestsService.recall(id);
      toast.success(isRTL ? '\u062a\u0645 \u0625\u0644\u063a\u0627\u0621 \u0627\u0644\u062a\u0642\u062f\u064a\u0645' : 'Request recalled');
      loadRequests();
    } catch (error: any) { toast.error(error.response?.data?.message || t.errorAction); }
  };

  const handleUnfulfill = async (id: number) => {
    const ok = await confirm({ message: isRTL ? '\u0625\u0644\u063a\u0627\u0621 \u0627\u0644\u062a\u062d\u0648\u064a\u0644 \u0644\u0623\u0648\u0627\u0645\u0631 \u0634\u0631\u0627\u0621\u061f' : 'Reverse purchase order conversion?', severity: 'warning' });
    if (!ok) return;
    try {
      await branchPurchaseRequestsService.unfulfill(id);
      toast.success(isRTL ? '\u062a\u0645 \u0625\u0644\u063a\u0627\u0621 \u0627\u0644\u062a\u062d\u0648\u064a\u0644' : 'Conversion reversed');
      loadRequests();
    } catch (error: any) { toast.error(error.response?.data?.message || t.errorAction); }
  };

  const canDelete = userPermissions.includes('BRANCH_PURCHASE_REQUEST_APPROVE') || false;

  const openRejectDialog = (id: number) => {
    setRejectRequestId(id);
    setRejectReason('');
    setRejectOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectRequestId) return;
    if (!rejectReason.trim()) {
      toast.error(isRTL ? 'يرجى إدخال سبب الرفض' : 'Please enter a rejection reason');
      return;
    }
    try {
      await branchPurchaseRequestsService.reject(rejectRequestId, rejectReason);
      toast.success(t.successReject);
      setRejectOpen(false);
      setRejectRequestId(null);
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  const handleUnapprove = async (id: number) => {
    const ok = await confirm({ message: isRTL ? 'إلغاء الموافقة على هذا الطلب؟' : 'Unapprove this request?', severity: 'warning' });
    if (!ok) return;
    try {
      // Unapprove = reject without reason (goes back to Submitted)
      await branchPurchaseRequestsService.reject(id, 'Unapproved by central buyer');
      toast.success(isRTL ? 'تم إلغاء الموافقة' : 'Request unapproved');
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  const handlePrint = (req: BranchPurchaseRequest) => {
    const printContent = `
      <html dir="${isRTL ? 'rtl' : 'ltr'}">
      <head><title>${req.requestNumber}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        h2 { text-align: center; margin-bottom: 5px; }
        .info { display: flex; justify-content: space-between; margin: 15px 0; }
        .info div { flex: 1; }
        table { width: 100%; border-collapse: collapse; margin-top: 15px; }
        th, td { border: 1px solid #333; padding: 8px; text-align: ${isRTL ? 'right' : 'left'}; }
        th { background: #1976d2; color: white; }
        .footer { margin-top: 30px; display: flex; justify-content: space-between; }
        @media print { body { margin: 0; } }
      </style></head>
      <body>
        <h2>${isRTL ? 'طلب شراء مركزي' : 'Central Purchase Request'}</h2>
        <h3 style="text-align:center; color:#666;">${req.requestNumber}</h3>
        <div class="info">
          <div><strong>${isRTL ? 'الفرع' : 'Branch'}:</strong> ${isRTL ? req.branchNameAr || req.branchName : req.branchName}</div>
          <div><strong>${isRTL ? 'التاريخ' : 'Date'}:</strong> ${new Date(req.requestDate).toLocaleDateString('en-GB')}</div>
          <div><strong>${isRTL ? 'الحالة' : 'Status'}:</strong> ${req.status}</div>
        </div>
        <div class="info">
          <div><strong>${isRTL ? 'التاريخ المطلوب' : 'Required Date'}:</strong> ${req.requiredDate ? new Date(req.requiredDate).toLocaleDateString('en-GB') : '-'}</div>
          <div><strong>${isRTL ? 'ملاحظات' : 'Notes'}:</strong> ${req.notes || '-'}</div>
        </div>
        <table>
          <thead><tr>
            <th>#</th>
            <th>${isRTL ? 'الصنف' : 'Item'}</th>
            <th>${isRTL ? 'الوحدة' : 'Unit'}</th>
            <th>${isRTL ? 'الكمية' : 'Qty'}</th>
            <th>${isRTL ? 'السعر' : 'Price'}</th>
            <th>${isRTL ? 'الإجمالي' : 'Total'}</th>
            <th>${isRTL ? 'الأولوية' : 'Priority'}</th>
            <th>${isRTL ? 'ملاحظات' : 'Notes'}</th>
          </tr></thead>
          <tbody>
            ${req.details?.map((d, i) => {
              const price = priceEdits[d.itemId] || 0;
              const total = d.requestedQuantity * price;
              return `<tr>
              <td>${i + 1}</td>
              <td>${isRTL ? d.itemNameAr || d.itemName : d.itemName}</td>
              <td>${isRTL ? d.unitNameAr || d.unitName : d.unitName}</td>
              <td>${d.requestedQuantity.toFixed(3)}</td>
              <td>${price > 0 ? price.toFixed(3) : '-'}</td>
              <td>${total > 0 ? total.toFixed(3) : '-'}</td>
              <td>${d.priority}</td>
              <td>${d.notes || ''}</td>
            </tr>`;
            }).join('') || ''}
          </tbody>
          <tfoot><tr style="font-weight:bold; background:#e3f2fd;">
            <td colspan="5" style="text-align:${isRTL ? 'left' : 'right'}">${isRTL ? 'الإجمالي الكلي' : 'Grand Total'}</td>
            <td>${(req.details?.reduce((sum, d) => sum + d.requestedQuantity * (priceEdits[d.itemId] || 0), 0) || 0).toFixed(3)} KWD</td>
            <td colspan="2"></td>
          </tr></tfoot>
        </table>
        <div class="footer">
          <div><strong>${isRTL ? 'طالب الشراء' : 'Requested By'}:</strong> _______________</div>
          <div><strong>${isRTL ? 'المشتري' : 'Purchaser'}:</strong> _______________</div>
        </div>
      </body></html>`;
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => printWindow.print(), 300);
    }
  };

  const handleView = async (req: BranchPurchaseRequest) => {
    try {
      const full = await branchPurchaseRequestsService.getById(req.requestId);
      setViewRequest(full);
      setViewOpen(true);
    } catch {
      toast.error(t.errorAction);
    }
  };

  // ---- Consolidated Tab ----
  const loadConsolidated = useCallback(async () => {
    if (!filterCustodyId) return;
    setConsolidatedLoading(true);
    try {
      const data = await branchPurchaseRequestsService.getConsolidated(
        filterCustodyId as number,
        filterDateFrom || undefined,
        filterDateTo || undefined,
      );
      setConsolidated(data);
      // Collect all requestIds from branch breakdowns for selection
      const allIds = new Set<number>();
      data.items.forEach((item) =>
        item.branchBreakdown.forEach((bb) => allIds.add(bb.requestId)),
      );
      setSelectedRequestIds(allIds);
    } catch {
      toast.error(t.errorLoadData);
    } finally {
      setConsolidatedLoading(false);
    }
  }, [filterCustodyId, filterDateFrom, filterDateTo]);

  useEffect(() => {
    if (activeTab === 1) loadConsolidated();
  }, [activeTab, loadConsolidated]);

  const toggleExpandItem = (itemId: number) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });
  };

  const handleConvertToOrders = async () => {
    if (!filterCustodyId || selectedRequestIds.size === 0) return;
    const ok = await confirm({ message: t.convertToOrdersConfirm, severity: 'warning' });
    if (!ok) return;
    try {
      await branchPurchaseRequestsService.convertToOrders(
        filterCustodyId as number,
        Array.from(selectedRequestIds),
      );
      toast.success(t.successConvertOrders);
      loadConsolidated();
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  // ---- Pricing Tab ----
  const loadPricing = useCallback(async () => {
    if (!filterCustodyId) return;
    setPricingLoading(true);
    try {
      // ✅ FIX: use range endpoint that MERGES priced items + items from requests in the range
      const data = await branchPurchaseRequestsService.getDailyPricesRange(
        filterCustodyId as number,
        pricingFromDate,
        pricingToDate,
      );
      setDailyPrices(data);
      const edits: Record<number, number> = {};
      data.forEach((dp) => { edits[dp.itemId] = dp.unitPrice; });
      setPriceEdits(edits);
      // ✅ FIX: Trust consolidated as source of truth (it already aggregates across approved/fulfilled).
      // Use totalApprovedQuantity, but fall back to totalRequestedQuantity when ApprovedQuantity is null/0
      // (matches the "Total Qty" column in Shopping List).
      try {
        const consData = await branchPurchaseRequestsService.getConsolidated(filterCustodyId as number, pricingFromDate, pricingToDate);
        const qtyMap: Record<number, number> = {};
        consData.items.forEach((ci) => {
          const qty = ci.totalApprovedQuantity > 0 ? ci.totalApprovedQuantity : ci.totalRequestedQuantity;
          qtyMap[ci.itemId] = (qtyMap[ci.itemId] || 0) + qty;
        });
        setPoQuantities(qtyMap);
      } catch { /* ignore */ }
    } catch {
      toast.error(t.errorLoadData);
    } finally {
      setPricingLoading(false);
    }
  }, [filterCustodyId, pricingFromDate, pricingToDate]);

  useEffect(() => {
    if (activeTab === 2) {
      loadPricing();
      if (!consolidated && filterCustodyId) loadConsolidated();
    }
  }, [activeTab, loadPricing]);

  const handlePriceChange = (itemId: number, price: number) => {
    setPriceEdits((prev) => ({ ...prev, [itemId]: price }));
  };

  const handleSinglePriceApply = async () => {
    if (!filterCustodyId || !singlePriceItemId || singlePriceValue <= 0) return;
    try {
      const item = items.find((i: any) => i.itemId === singlePriceItemId);
      // Find the unitId for this item from daily prices or first available unit
      const existingDp = dailyPrices.find(dp => dp.itemId === singlePriceItemId);
      const unitId = existingDp?.unitId || item?.baseUnitId || 2; // fallback to KG

      // ✅ FIX: ONE call with full range — backend expands to day records.
      await branchPurchaseRequestsService.setDailyPrice({
        itemId: singlePriceItemId,
        unitId: unitId,
        unitPrice: singlePriceValue,
        fromDate: singlePriceFrom,
        toDate: singlePriceTo,
        custodyId: filterCustodyId as number,
      });
      toast.success(isRTL ? 'تم تطبيق السعر بنجاح' : 'Price applied successfully');
      setSinglePriceValue(0);
      loadPricing();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  const handleApplyPricing = async () => {
    if (!filterCustodyId) return;
    setPricingLoading(true);
    try {
      // ✅ FIX: ONE API call per item with the full range — backend expands to day records internally.
      // (Avoids the N×M sequential calls that froze the UI for large ranges.)
      // Allow price=0 to support resetting/clearing prices.
      const tasks: Promise<unknown>[] = [];
      for (const dp of dailyPrices) {
        const price = priceEdits[dp.itemId];
        if (price != null && price >= 0) {
          tasks.push(
            branchPurchaseRequestsService.setDailyPrice({
              itemId: dp.itemId,
              unitId: dp.unitId,
              unitPrice: price,
              fromDate: pricingFromDate,
              toDate: pricingToDate,
              custodyId: filterCustodyId as number,
            }).catch(() => { /* skip individual conflicts */ })
          );
        }
      }
      await Promise.all(tasks);

      // Then apply prices to purchase orders
      await branchPurchaseRequestsService.applyPricing({
        custodyId: filterCustodyId as number,
        fromDate: pricingFromDate,
        toDate: pricingToDate,
      });
      toast.success(t.successPricing);
      loadPricing();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    } finally {
      setPricingLoading(false);
    }
  };

  const handleConvertToInvoices = async () => {
    if (!filterCustodyId) return;
    const ok = await confirm({ message: t.convertToInvoicesConfirm, severity: 'warning' });
    if (!ok) return;
    try {
      // Apply pricing first
      await branchPurchaseRequestsService.applyPricing({
        custodyId: filterCustodyId as number,
        fromDate: pricingFromDate,
        toDate: pricingToDate,
      });
      // Convert all approved POs for this custody - backend finds PO IDs automatically
      await branchPurchaseRequestsService.convertToInvoices(filterCustodyId as number, []);
      toast.success(t.successConvertInvoices);
      loadPricing();
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  // ---- Render helpers ----
  const renderStatsCards = () => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalRequests}</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.total}</Typography>
            </Box>
            <ShoppingCartIcon sx={{ fontSize: 50, opacity: 0.3 }} />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.pendingApproval}</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.pending}</Typography>
            </Box>
            <ScheduleIcon sx={{ fontSize: 50, opacity: 0.3 }} />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.approvedCount}</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.approved}</Typography>
            </Box>
            <CheckCircleIcon sx={{ fontSize: 50, opacity: 0.3 }} />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ background: 'linear-gradient(135deg, #38a169 0%, #276749 100%)', color: 'white', position: 'relative', overflow: 'hidden' }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.fulfilledCount}</Typography>
              <Typography variant="h3" sx={{ fontWeight: 700 }}>{stats.fulfilled}</Typography>
            </Box>
            <FulfilledIcon sx={{ fontSize: 50, opacity: 0.3 }} />
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  const renderFilterBar = () => (
    <Paper
      elevation={3}
      sx={{
        mt: 2,
        mb: 3,
        p: 2,
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(135deg, #1e3a5f 0%, #2c5282 100%)'
          : 'linear-gradient(135deg, #fce7f3 0%, #ddd6fe 100%)',
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
        <TextField
          size="small"
          placeholder={t.search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          size="small"
          options={branches}
          getOptionLabel={(o: any) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
          value={branches.find((x: any) => x.branchId === filterBranchId) || null}
          onChange={(_: any, v: any) => setFilterBranchId(v?.branchId || '')}
          renderInput={(params) => <TextField {...params} label={t.filterByBranch} />}
          sx={{ minWidth: 200 }}
        />
        <Autocomplete
          size="small"
          options={custodies}
          getOptionLabel={(o: any) => `${(o as any).custodyCode || o.custodyNumber} - ${isRTL ? ((o as any).employeeNameAr || o.employeeName) : o.employeeName}`}
          value={custodies.find((x: any) => x.custodyId === filterCustodyId) || null}
          onChange={(_: any, v: any) => setFilterCustodyId(v?.custodyId || '')}
          renderInput={(params) => <TextField {...params} label={t.filterByCustody} />}
          sx={{ minWidth: 220 }}
        />
        <TextField
          select
          size="small"
          label={t.filterByStatus}
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">{t.all}</MenuItem>
          <MenuItem value="Draft">{t.draft}</MenuItem>
          <MenuItem value="Submitted">{t.submitted}</MenuItem>
          <MenuItem value="Approved">{t.approved}</MenuItem>
          <MenuItem value="Rejected">{t.rejected}</MenuItem>
          <MenuItem value="Fulfilled">{t.fulfilled}</MenuItem>
        </TextField>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Tooltip title={isRTL ? 'الفلترة بتاريخ التسليم المطلوب (Required Date)، أو تاريخ السند إذا لم يُحدّد' : 'Filters by Required Date (falls back to Request Date)'} arrow>
            <DatePicker
              label={isRTL ? 'من تاريخ (تسليم مطلوب)' : 'From (Required Date)'}
              value={filterDateFrom ? new Date(filterDateFrom) : null}
              onChange={(date) => setFilterDateFrom(date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '')}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 175 } } }}
            />
          </Tooltip>
          <DatePicker
            label={isRTL ? 'إلى تاريخ (تسليم مطلوب)' : 'To (Required Date)'}
            value={filterDateTo ? new Date(filterDateTo) : null}
            onChange={(date) => setFilterDateTo(date ? `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}` : '')}
            format="dd/MM/yyyy"
            minDate={minDate}
            maxDate={maxDate}
            slotProps={{ textField: { size: 'small', sx: { minWidth: 175 } } }}
          />
        </LocalizationProvider>
      </Stack>
    </Paper>
  );

  // ---- Tab 0: My Requests ----
  const renderRequestsTable = () => (
    <TableContainer component={Paper} elevation={3}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ background: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
            <TableCell sx={{ fontWeight: 700 }}>{t.requestNumber}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t.branch}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t.custody}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t.date}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t.requiredDate}</TableCell>
            <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>{isRTL ? 'أمر/فاتورة' : 'PO / Invoice'}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t.status}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t.itemsCount}</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="center">{isRTL ? 'مسعّر' : 'Priced'}</TableCell>
            <TableCell sx={{ fontWeight: 700 }} align="right">{isRTL ? 'التكلفة' : 'Total Cost'}</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>{t.actions}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : filteredRequests.length === 0 ? (
            <TableRow>
              <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">{t.noData}</Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedRequests.map((req) => (
              <TableRow
                key={req.requestId}
                hover
                sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {req.requestNumber}
                  </Typography>
                </TableCell>
                <TableCell>{isRTL ? req.branchNameAr : req.branchName}</TableCell>
                <TableCell>{req.custodyName || '-'}</TableCell>
                <TableCell>{formatDate(req.requestDate)}</TableCell>
                <TableCell>{formatDate(req.requiredDate)}</TableCell>
                <TableCell sx={{ minWidth: 110 }}>
                  {(req.poNumber || req.invoiceNumber) ? (
                    <Stack spacing={0.3}>
                      {req.poNumber && (
                        <Chip
                          label={req.poNumber}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: { xs: '0.65rem', sm: '0.7rem' }, '& .MuiChip-label': { px: 0.8 } }}
                        />
                      )}
                      {req.invoiceNumber && (
                        <Chip
                          label={req.invoiceNumber}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontSize: { xs: '0.65rem', sm: '0.7rem' }, '& .MuiChip-label': { px: 0.8 } }}
                        />
                      )}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusLabel(req.status)}
                    color={getStatusColor(req.status)}
                    size="small"
                    variant="filled"
                  />
                </TableCell>
                <TableCell>{req.details?.length || 0}</TableCell>
                <TableCell align="center">
                  {(() => {
                    // ✅ FIX: look up each item's price by THIS REQUEST'S RequiredDate (or RequestDate)
                    const showPrice = req.status === 'Approved' || req.status === 'Fulfilled';
                    if (!showPrice) return <Typography variant="body2" color="text.secondary">-</Typography>;
                    const reqDate = (req.requiredDate || req.requestDate)?.split('T')[0] || '';
                    const allPriced = req.details && req.details.length > 0 && req.details.every(d => (pricesByDate[`${d.itemId}_${reqDate}`] || 0) > 0);
                    return allPriced
                      ? <CheckCircleIcon fontSize="small" sx={{ color: '#4CAF50' }} />
                      : <CancelIcon fontSize="small" sx={{ color: '#f44336' }} />;
                  })()}
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    // ✅ FIX: total uses each request's own day price
                    const showPrice = req.status === 'Approved' || req.status === 'Fulfilled';
                    if (!showPrice) return <Typography variant="body2" color="text.secondary">-</Typography>;
                    const reqDate = (req.requiredDate || req.requestDate)?.split('T')[0] || '';
                    const total = req.details?.reduce((sum, d) => {
                      const price = pricesByDate[`${d.itemId}_${reqDate}`] || 0;
                      return sum + (d.requestedQuantity * price);
                    }, 0) || 0;
                    return total > 0 ? <Typography variant="body2" sx={{ fontWeight: 700, color: '#4CAF50' }}>{total.toFixed(3)} KWD</Typography> : <Typography variant="body2" color="text.secondary">-</Typography>;
                  })()}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5}>
                    <Tooltip title={t.view}>
                      <IconButton size="small" color="info" onClick={() => handleView(req)}>
                        <ViewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {req.status === 'Draft' && (
                      <>
                        <Tooltip title={t.edit}>
                          <IconButton size="small" color="primary" onClick={() => openEditDialog(req)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.delete}>
                          <IconButton size="small" color="error" onClick={() => handleDelete(req.requestId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.submit}>
                          <IconButton size="small" color="warning" onClick={() => handleSubmit(req.requestId)}>
                            <SubmitIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {req.status === 'Submitted' && isCentralBuyer && (
                      <>
                        <Tooltip title={t.approve}>
                          <IconButton size="small" color="success" onClick={() => handleApprove(req.requestId)}>
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t.reject}>
                          <IconButton size="small" color="error" onClick={() => openRejectDialog(req.requestId)}>
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {req.status === 'Submitted' && req.requestedBy === userId && (
                      <Tooltip title={isRTL ? '\u0625\u0644\u063a\u0627\u0621 \u0627\u0644\u062a\u0642\u062f\u064a\u0645' : 'Recall'}>
                        <IconButton size="small" color="warning" onClick={() => handleRecall(req.requestId)}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {req.status === 'Fulfilled' && isCentralBuyer && (
                      <Tooltip title={isRTL ? '\u0625\u0644\u063a\u0627\u0621 \u0627\u0644\u062a\u062d\u0648\u064a\u0644' : 'Reverse Conversion'}>
                        <IconButton size="small" color="warning" onClick={() => handleUnfulfill(req.requestId)}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {req.status === 'Approved' && isCentralBuyer && (
                      <>
                        <Tooltip title={isRTL ? 'تعديل' : 'Edit'}>
                          <IconButton size="small" color="primary" onClick={() => openEditDialog(req)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={isRTL ? 'إلغاء الموافقة' : 'Unapprove'}>
                          <IconButton size="small" color="warning" onClick={() => handleUnapprove(req.requestId)}>
                            <UndoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {req.status !== 'Closed' && req.status !== 'Draft' && canDelete && (
                      <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(req.requestId)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {req.status !== 'Draft' && (
                      <Tooltip title={isRTL ? 'طباعة' : 'Print'}>
                        <IconButton size="small" sx={{ color: '#607D8B' }} onClick={() => handlePrint(req)}>
                          <PrintIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {filteredRequests.length > 0 && (
        <TablePagination
          component="div"
          count={filteredRequests.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage={isRTL ? 'صفوف في الصفحة:' : 'Rows per page:'}
          labelDisplayedRows={({ from, to, count }) =>
            isRTL ? `${from}–${to} من ${count}` : `${from}–${to} of ${count}`
          }
          sx={{ direction: isRTL ? 'rtl' : 'ltr' }}
        />
      )}
    </TableContainer>
  );

  // ---- Tab 1: Consolidated View ----
  const renderConsolidatedView = () => (
    <Box>
      {!filterCustodyId ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {isRTL ? 'يرجى اختيار العهدة لعرض البيانات المجمّعة' : 'Please select a custody to view consolidated data'}
          </Typography>
        </Paper>
      ) : consolidatedLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !consolidated ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">{t.noData}</Typography>
        </Paper>
      ) : (
        <>
          {/* Summary cards */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalRequests}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{consolidated.totalRequests}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalBranches}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{consolidated.totalBranches}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #38a169 0%, #276749 100%)', color: 'white' }}>
              <CardContent>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>{t.totalItems}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 700 }}>{consolidated.totalItems}</Typography>
              </CardContent>
            </Card>
          </Box>

          <TableContainer component={Paper} elevation={3}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
                  <TableCell sx={{ width: 40 }} />
                  <TableCell sx={{ fontWeight: 700 }}>{t.item}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.unit}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.totalQty}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.approvedQty}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{t.currentPrice}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {consolidated.items.map((ci) => (
                  <React.Fragment key={ci.itemId}>
                    <TableRow hover sx={{ cursor: 'pointer' }} onClick={() => toggleExpandItem(ci.itemId)}>
                      <TableCell>
                        <IconButton size="small">
                          {expandedItems.has(ci.itemId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {ci.itemCode} - {isRTL ? ci.itemNameAr : ci.itemName}
                        </Typography>
                      </TableCell>
                      <TableCell>{isRTL ? ci.unitNameAr : ci.unitName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {ci.totalRequestedQuantity.toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {ci.totalApprovedQuantity.toFixed(3)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {ci.currentPrice != null ? ci.currentPrice.toFixed(3) : '-'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={6} sx={{ p: 0 }}>
                        <Collapse in={expandedItems.has(ci.itemId)} timeout="auto" unmountOnExit>
                          <Box sx={{ p: 2, pl: 6, background: theme.palette.action.hover }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                              {t.branchBreakdown}
                            </Typography>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>{t.branch}</TableCell>
                                  <TableCell>{t.requestNumber}</TableCell>
                                  <TableCell>{t.requestedQty}</TableCell>
                                  <TableCell>{t.approvedQty}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {ci.branchBreakdown.map((bb) => (
                                  <TableRow key={`${bb.branchId}-${bb.requestId}`}>
                                    <TableCell>{isRTL ? bb.branchNameAr : bb.branchName}</TableCell>
                                    <TableCell>{bb.requestNumber}</TableCell>
                                    <TableCell>{bb.requestedQuantity.toFixed(3)}</TableCell>
                                    <TableCell>{bb.approvedQuantity != null ? bb.approvedQuantity.toFixed(3) : '-'}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ConsolidateIcon />}
              onClick={handleConvertToOrders}
              disabled={selectedRequestIds.size === 0}
              sx={{
                background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                '&:hover': { background: 'linear-gradient(45deg, #5a6fd6 30%, #6a4296 90%)' },
              }}
            >
              {t.convertToOrders}
            </Button>
          </Box>
        </>
      )}
    </Box>
  );

  // ---- Tab 2: Daily Pricing ----
  const renderDailyPricing = () => (
    <Box>
      {!filterCustodyId ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {isRTL ? 'يرجى اختيار العهدة لعرض التسعير' : 'Please select a custody to view pricing'}
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 2, mb: 3 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Stack direction="row" spacing={2} alignItems="center">
                <DatePicker
                  label={t.fromDate}
                  value={pricingFromDate ? new Date(pricingFromDate) : null}
                  onChange={(date) => setPricingFromDate(date ? date.toISOString().split('T')[0] : '')}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                />
                <DatePicker
                  label={t.toDate}
                  value={pricingToDate ? new Date(pricingToDate) : null}
                  onChange={(date) => setPricingToDate(date ? date.toISOString().split('T')[0] : '')}
                  format="dd/MM/yyyy"
                  minDate={minDate}
                  maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
                />
                <Button variant="outlined" onClick={loadPricing} startIcon={<RefreshIcon />}>
                  {t.refresh}
                </Button>
              </Stack>
            </LocalizationProvider>
          </Paper>

          {/* Single Item Pricing */}
          <Paper sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'primary.main', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: 'primary.main' }}>
              {isRTL ? 'تسعير صنف واحد لفترة محددة' : 'Price Single Item for a Period'}
            </Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
              <Autocomplete
                size="small"
                options={items}
                getOptionLabel={(o: any) => `${o.itemCode} - ${isRTL ? o.itemNameAr || o.itemName : o.itemName}`}
                value={items.find((i: any) => i.itemId === singlePriceItemId) || null}
                onChange={(_: any, v: any) => setSinglePriceItemId(v?.itemId || null)}
                sx={{ minWidth: 250 }}
                renderInput={(params) => <TextField {...params} label={isRTL ? 'اختر الصنف' : 'Select Item'} />}
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={isRTL ? 'من تاريخ' : 'From'}
                  value={singlePriceFrom ? new Date(singlePriceFrom) : null}
                  onChange={(d) => setSinglePriceFrom(d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '')}
                  format="dd/MM/yyyy" minDate={minDate} maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
                />
                <DatePicker
                  label={isRTL ? 'إلى تاريخ' : 'To'}
                  value={singlePriceTo ? new Date(singlePriceTo) : null}
                  onChange={(d) => setSinglePriceTo(d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}` : '')}
                  format="dd/MM/yyyy" minDate={minDate} maxDate={maxDate}
                  slotProps={{ textField: { size: 'small', sx: { minWidth: 140 } } }}
                />
              </LocalizationProvider>
              <TextField
                size="small"
                label={isRTL ? 'السعر' : 'Price'}
                value={singlePriceValue === 0 ? '' : String(singlePriceValue)}
                onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) setSinglePriceValue(v === '' || v === '.' ? 0 : parseFloat(v)); }}
                inputProps={{ inputMode: 'decimal' }}
                sx={{ width: 120 }}
              />
              <Button
                variant="contained"
                color="primary"
                size="small"
                disabled={!singlePriceItemId || !singlePriceFrom || !singlePriceTo || singlePriceValue <= 0}
                onClick={handleSinglePriceApply}
              >
                {isRTL ? 'تطبيق' : 'Apply'}
              </Button>
            </Stack>
          </Paper>

          {pricingLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : dailyPrices.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t.noPricingData}</Typography>
            </Paper>
          ) : (
            <>
              <TableContainer component={Paper} elevation={3}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
                      <TableCell sx={{ fontWeight: 700 }}>{t.item}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t.unit}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t.quantity}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{isRTL ? 'السعر' : 'Unit Price'}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }} align="right">{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dailyPrices.map((dp) => {
                      const price = priceEdits[dp.itemId] ?? dp.unitPrice;
                      // Find quantity from consolidated data
                      // ✅ FIX: fall back to totalRequestedQuantity if approved is 0/null
                      const consItem = consolidated?.items.find(ci => ci.itemId === dp.itemId);
                      const fallbackQty = consItem ? (consItem.totalApprovedQuantity > 0 ? consItem.totalApprovedQuantity : consItem.totalRequestedQuantity) : 0;
                      const qty = poQuantities[dp.itemId] || fallbackQty;
                      const total = qty * price;
                      return (
                      <TableRow key={dp.priceId} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dp.itemCode} - {isRTL ? dp.itemNameAr : dp.itemName}
                          </Typography>
                        </TableCell>
                        <TableCell>{isRTL ? dp.unitNameAr : dp.unitName}</TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {qty.toFixed(3)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={price}
                            onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handlePriceChange(dp.itemId, v === '' || v === '.' ? 0 : parseFloat(v)); }}
                            inputProps={{ inputMode: 'decimal' }}
                            sx={{ width: 130 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700, color: total > 0 ? '#4CAF50' : 'text.secondary' }}>
                            {total > 0 ? `${total.toFixed(3)} KWD` : '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>);
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Stack direction="row" spacing={2} sx={{ mt: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PriceChangeIcon />}
                  onClick={handleApplyPricing}
                  sx={{
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    '&:hover': { background: 'linear-gradient(45deg, #5a6fd6 30%, #6a4296 90%)' },
                  }}
                >
                  {t.applyPricing}
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<InvoiceIcon />}
                  onClick={handleConvertToInvoices}
                >
                  {t.convertToInvoices}
                </Button>
              </Stack>
            </>
          )}
        </>
      )}
    </Box>
  );

  // ---- Surplus Stock helpers ----
  const handleAddSurplusLine = () => {
    setSurplusLines((prev) => [
      ...prev,
      {
        id: `surplus-${surplusLineKeyCounter}`,
        itemId: 0,
        unitId: 0,
        quantity: 0,
        unitPrice: 0,
        notes: '',
        availableUnits: [],
      },
    ]);
    setSurplusLineKeyCounter((c) => c + 1);
  };

  const handleRemoveSurplusLine = (id: string) => {
    setSurplusLines((prev) => prev.filter((l) => l.id !== id));
  };

  const handleSurplusLineItemChange = async (id: string, itemId: number | null) => {
    let units: ItemUnit[] = [];
    if (itemId) {
      try {
        units = await itemUnitsService.getItemUnits(itemId);
      } catch { /* ignore */ }
    }
    setSurplusLines((prev) =>
      prev.map((l) =>
        l.id === id
          ? {
              ...l,
              itemId: itemId || 0,
              unitId: units.length > 0 ? units[0].unitId : 0,
              unitPrice: itemId ? (priceEdits[itemId] || 0) : 0,
              availableUnits: units,
            }
          : l,
      ),
    );
  };

  const handleSurplusLineChange = (id: string, field: string, value: unknown) => {
    setSurplusLines((prev) =>
      prev.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    );
  };

  const handleCreateSurplusInvoice = async () => {
    if (!surplusWarehouseId || surplusLines.length === 0) return;
    const validLines = surplusLines.filter(l => l.itemId && l.unitId && l.quantity > 0 && l.unitPrice > 0);
    if (validLines.length === 0) {
      toast.error(isRTL ? 'يرجى إضافة أصناف صحيحة' : 'Please add valid items');
      return;
    }
    setSurplusCreating(true);
    try {
      const custodyId = filterCustodyId as number;
      await axiosInstance.post('/PurchaseInvoices', {
        warehouseId: surplusWarehouseId,
        custodyId: custodyId,
        paymentType: 'Custody',
        invoiceDate: new Date().toISOString().split('T')[0],
        details: validLines.map((l, idx) => ({
          lineNumber: idx + 1,
          itemId: l.itemId,
          unitId: l.unitId,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
        })),
      });
      toast.success(t.surplusInvoiceCreated);
      setSurplusLines([]);
      setSurplusWarehouseId('');
      loadSurplusInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    } finally {
      setSurplusCreating(false);
    }
  };

  const loadSurplusInvoices = useCallback(async () => {
    if (!filterCustodyId) return;
    setSurplusInvoicesLoading(true);
    try {
      const data = await branchPurchaseRequestsService.getSurplusInvoices(
        filterCustodyId as number, surplusFromDate, surplusToDate
      );
      setSurplusInvoices(data);
    } catch { setSurplusInvoices([]); }
    finally { setSurplusInvoicesLoading(false); }
  }, [filterCustodyId, surplusFromDate, surplusToDate]);

  useEffect(() => {
    if (activeTab === 3) loadSurplusInvoices();
  }, [activeTab, loadSurplusInvoices]);

  const handleDeleteSurplusInvoice = async (invoiceId: number) => {
    const ok = await confirm({ message: isRTL ? '\u0647\u0644 \u062a\u0631\u064a\u062f \u062d\u0630\u0641 \u0647\u0630\u0647 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629\u061f' : 'Delete this invoice?', severity: 'error' });
    if (!ok) return;
    try {
      await axiosInstance.delete(`/PurchaseInvoices/${invoiceId}`);
      toast.success(isRTL ? '\u062a\u0645 \u062d\u0630\u0641 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629' : 'Invoice deleted');
      loadSurplusInvoices();
    } catch (error: any) {
      toast.error(error.response?.data?.message || t.errorAction);
    }
  };

  // ---- Tab 3: Surplus Stock ----
  const renderSurplusStock = () => (
    <Box>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <InventoryIcon color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {t.surplusDescription}
          </Typography>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <Autocomplete
            size="small"
            options={mainWarehouses}
            getOptionLabel={(o: any) => isRTL ? (o.warehouseNameAr || o.warehouseName) : o.warehouseName}
            value={mainWarehouses.find((x: any) => x.warehouseId === surplusWarehouseId) || null}
            onChange={(_: any, v: any) => setSurplusWarehouseId(v?.warehouseId || '')}
            renderInput={(params) => <TextField {...params} label={t.selectWarehouse} />}
            sx={{ minWidth: 250 }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddSurplusLine}
          >
            {t.addSurplusItem}
          </Button>
        </Stack>
      </Paper>

      {surplusLines.length > 0 && (
        <>
          <TableContainer component={Paper} elevation={3}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
                  <TableCell sx={{ fontWeight: 700, width: 40 }}>{t.lineNumber}</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 250 }}>{t.item}</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>{t.unit}</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>{t.quantity}</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>{t.unitPrice}</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 120 }} align="right">{t.total}</TableCell>
                  <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>{t.notes}</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 50 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {surplusLines.map((line, idx) => {
                  const lineTotal = line.quantity * line.unitPrice;
                  return (
                    <TableRow key={line.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Autocomplete
                          size="small"
                          options={items}
                          getOptionLabel={(opt) =>
                            `${opt.itemCode} - ${isRTL ? opt.itemNameAr : opt.itemName}`
                          }
                          value={items.find((i) => i.itemId === line.itemId) || null}
                          onChange={(_, val) => handleSurplusLineItemChange(line.id, val?.itemId || null)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder={t.selectItem} />
                          )}
                          isOptionEqualToValue={(opt, val) => opt.itemId === val.itemId}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={line.unitId || ''}
                          onChange={(e) =>
                            handleSurplusLineChange(line.id, 'unitId', e.target.value ? Number(e.target.value) : 0)
                          }
                        >
                          <MenuItem value="" disabled>{t.selectUnit}</MenuItem>
                          {(line.availableUnits || []).map((u) => (
                            <MenuItem key={u.unitId} value={u.unitId}>
                              {isRTL ? u.unitNameAr : u.unitName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={line.quantity === 0 ? '' : String(line.quantity)}
                          onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handleSurplusLineChange(line.id, 'quantity', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                          inputProps={{ inputMode: 'decimal' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={line.unitPrice === 0 ? '' : String(line.unitPrice)}
                          onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handleSurplusLineChange(line.id, 'unitPrice', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                          inputProps={{ inputMode: 'decimal' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 700, color: lineTotal > 0 ? '#4CAF50' : 'text.secondary' }}>
                          {lineTotal > 0 ? `${lineTotal.toFixed(3)} KWD` : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={line.notes}
                          onChange={(e) => handleSurplusLineChange(line.id, 'notes', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t.removeLine}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveSurplusLine(line.id)}
                          >
                            <RemoveLineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {/* Grand total row */}
                <TableRow sx={{ background: theme.palette.action.hover }}>
                  <TableCell colSpan={5} align={isRTL ? 'left' : 'right'}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {isRTL ? 'الإجمالي الكلي' : 'Grand Total'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#4CAF50' }}>
                      {surplusLines.reduce((sum, l) => sum + l.quantity * l.unitPrice, 0).toFixed(3)} KWD
                    </Typography>
                  </TableCell>
                  <TableCell colSpan={2} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={surplusCreating ? <CircularProgress size={18} color="inherit" /> : <InvoiceIcon />}
              onClick={handleCreateSurplusInvoice}
              disabled={surplusCreating || !surplusWarehouseId || surplusLines.length === 0}
              sx={{
                background: 'linear-gradient(45deg, #38a169 30%, #276749 90%)',
                '&:hover': { background: 'linear-gradient(45deg, #2f855a 30%, #1e5138 90%)' },
                px: 4,
                py: 1.5,
              }}
            >
              {t.createSurplusInvoice}
            </Button>
          </Box>
        </>
      )}

      {/* Existing Surplus Invoices */}
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
        {isRTL ? '\u0641\u0648\u0627\u062a\u064a\u0631 \u0627\u0644\u0645\u062e\u0632\u0648\u0646 \u0627\u0644\u0632\u0627\u0626\u062f' : 'Surplus Invoices'}
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={isRTL ? '\u0645\u0646 \u062a\u0627\u0631\u064a\u062e' : 'From Date'}
              value={surplusFromDate ? new Date(surplusFromDate) : null}
              onChange={(d) => setSurplusFromDate(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '')}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 155 } } }}
            />
            <DatePicker
              label={isRTL ? '\u0625\u0644\u0649 \u062a\u0627\u0631\u064a\u062e' : 'To Date'}
              value={surplusToDate ? new Date(surplusToDate) : null}
              onChange={(d) => setSurplusToDate(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : '')}
              format="dd/MM/yyyy"
              minDate={minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 155 } } }}
            />
          </LocalizationProvider>
          <Button variant="outlined" onClick={loadSurplusInvoices} startIcon={<RefreshIcon />} size="small">
            {isRTL ? '\u062a\u062d\u062f\u064a\u062b' : 'Refresh'}
          </Button>
        </Stack>
      </Paper>

      {surplusInvoicesLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
      ) : surplusInvoices.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary">{isRTL ? '\u0644\u0627 \u062a\u0648\u062c\u062f \u0641\u0648\u0627\u062a\u064a\u0631' : 'No surplus invoices found'}</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={2}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: theme.palette.mode === 'dark' ? '#1e293b' : '#f1f5f9' }}>
                <TableCell sx={{ fontWeight: 700 }}>{isRTL ? '\u0631\u0642\u0645 \u0627\u0644\u0641\u0627\u062a\u0648\u0631\u0629' : 'Invoice #'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{isRTL ? '\u0627\u0644\u062a\u0627\u0631\u064a\u062e' : 'Date'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{isRTL ? '\u0627\u0644\u0645\u062e\u0632\u0646' : 'Warehouse'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="right">{isRTL ? '\u0627\u0644\u0645\u0628\u0644\u063a' : 'Amount'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{isRTL ? '\u0627\u0644\u062d\u0627\u0644\u0629' : 'Status'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{isRTL ? '\u0627\u0644\u0623\u0635\u0646\u0627\u0641' : 'Items'}</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>{isRTL ? '\u0627\u0644\u0625\u062c\u0631\u0627\u0621\u0627\u062a' : 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {surplusInvoices.map((inv: any) => (
                <TableRow key={inv.purchaseInvoiceId} hover>
                  <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{inv.invoiceNumber}</Typography></TableCell>
                  <TableCell>{new Date(inv.invoiceDate).toLocaleDateString('en-GB')}</TableCell>
                  <TableCell>{isRTL ? (inv.warehouseNameAr || inv.warehouseName) : inv.warehouseName}</TableCell>
                  <TableCell align="right"><Typography sx={{ fontWeight: 700, color: '#4CAF50' }}>{(inv.totalAmount || 0).toFixed(3)} KWD</Typography></TableCell>
                  <TableCell><Chip label={inv.status} size="small" color={inv.status === 'Posted' ? 'success' : 'warning'} /></TableCell>
                  <TableCell>{inv.itemCount}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {inv.status === 'Draft' && canDelete && (
                        <Tooltip title={isRTL ? '\u062d\u0630\u0641' : 'Delete'}>
                          <IconButton size="small" color="error" onClick={() => handleDeleteSurplusInvoice(inv.purchaseInvoiceId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  // ---- Create/Edit Dialog ----
  const renderDialog = () => (
    <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="xl" fullWidth>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <ShoppingCartIcon />
        {editingRequest ? t.editRequest : t.newRequest}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <Stack spacing={2.5} sx={{ mt: 1 }}>
          {/* Header fields */}
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Autocomplete
              size="small"
              options={branches}
              getOptionLabel={(o: any) => isRTL ? (o.branchNameAr || o.branchName) : o.branchName}
              value={branches.find((x: any) => x.branchId === dialogBranchId) || null}
              onChange={(_: any, v: any) => setDialogBranchId(v?.branchId || '')}
              renderInput={(params) => <TextField {...params} label={t.selectBranch} required />}
              fullWidth
            />
            <Autocomplete
              size="small"
              options={custodies}
              getOptionLabel={(o: any) => `${(o as any).custodyCode || o.custodyNumber} - ${isRTL ? ((o as any).employeeNameAr || o.employeeName) : o.employeeName}`}
              value={custodies.find((x: any) => x.custodyId === dialogCustodyId) || null}
              onChange={(_: any, v: any) => setDialogCustodyId(v?.custodyId || '')}
              renderInput={(params) => <TextField {...params} label={t.selectCustody} />}
              fullWidth
            />
            <TextField
              type="date"
              label={t.requiredDate}
              value={dialogRequiredDate}
              onChange={(e) => setDialogRequiredDate(e.target.value)}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Stack>

          <TextField
            label={t.notes}
            value={dialogNotes}
            onChange={(e) => setDialogNotes(e.target.value)}
            multiline
            rows={2}
            fullWidth
            size="small"
          />

          {/* Detail lines */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.itemsCount}</Typography>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={handleAddLine}>
                {t.addLine}
              </Button>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: theme.palette.action.hover }}>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}>{t.lineNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 420 }}>{t.item}</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 130 }}>{t.unit}</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>{t.quantity}</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 110 }}>{t.priority}</TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>{t.notes}</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 50 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dialogLines.map((line, idx) => (
                    <TableRow key={line.key}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell sx={{ minWidth: 420 }}>
                        <Autocomplete
                          size="small"
                          fullWidth
                          options={items}
                          getOptionLabel={(opt) =>
                            `${opt.itemCode} - ${isRTL ? opt.itemNameAr : opt.itemName}`
                          }
                          value={items.find((i) => i.itemId === line.itemId) || null}
                          onChange={(_, val) => handleLineItemChange(line.key, val?.itemId || null)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder={t.selectItem} />
                          )}
                          isOptionEqualToValue={(opt, val) => opt.itemId === val.itemId}
                          renderOption={(props, opt) => (
                            <li {...props} key={opt.itemId} style={{ whiteSpace: 'normal' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {opt.itemCode}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {isRTL ? (opt.itemNameAr || opt.itemName) : opt.itemName}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          slotProps={{ paper: { sx: { minWidth: 380 } } }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={line.unitId || ''}
                          onChange={(e) =>
                            handleLineChange(line.key, 'unitId', e.target.value ? Number(e.target.value) : null)
                          }
                        >
                          <MenuItem value="" disabled>{t.selectUnit}</MenuItem>
                          {line.availableUnits.map((u) => (
                            <MenuItem key={u.unitId} value={u.unitId}>
                              {isRTL ? u.unitNameAr : u.unitName}
                            </MenuItem>
                          ))}
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={line.requestedQuantity === 0 ? '' : String(line.requestedQuantity)}
                          onChange={(e) => { const v = e.target.value; if (v === '' || /^\d*\.?\d*$/.test(v)) handleLineChange(line.key, 'requestedQuantity', v === '' || v === '.' ? 0 : parseFloat(v)); }}
                          inputProps={{ inputMode: 'decimal' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          value={line.priority}
                          onChange={(e) => handleLineChange(line.key, 'priority', e.target.value)}
                        >
                          <MenuItem value="High">{t.high}</MenuItem>
                          <MenuItem value="Medium">{t.medium}</MenuItem>
                          <MenuItem value="Low">{t.low}</MenuItem>
                        </TextField>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          fullWidth
                          value={line.notes}
                          onChange={(e) => handleLineChange(line.key, 'notes', e.target.value)}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={t.removeLine}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveLine(line.key)}
                          >
                            <RemoveLineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleDialogClose} color="inherit">
          {t.cancel}
        </Button>
        <Button
          variant="contained"
          onClick={handleSaveRequest}
          disabled={dialogSaving}
          startIcon={dialogSaving ? <CircularProgress size={18} /> : undefined}
          sx={{
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            '&:hover': { background: 'linear-gradient(45deg, #5a6fd6 30%, #6a4296 90%)' },
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ---- View Dialog ----
  const renderViewDialog = () => (
    <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #30cfd0 0%, #330867 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <ViewIcon />
        {t.requestDetails} - {viewRequest?.requestNumber}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        {viewRequest && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.branch}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {isRTL ? viewRequest.branchNameAr : viewRequest.branchName}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.custody}</Typography>
                <Typography variant="body1">{viewRequest.custodyName || '-'}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.status}</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={getStatusLabel(viewRequest.status)} color={getStatusColor(viewRequest.status)} size="small" />
                </Box>
              </Box>
            </Stack>

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.date}</Typography>
                <Typography variant="body1">{formatDate(viewRequest.requestDate)}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.requiredDate}</Typography>
                <Typography variant="body1">{formatDate(viewRequest.requiredDate)}</Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{t.requestedBy}</Typography>
                <Typography variant="body1">{viewRequest.requestedByName || '-'}</Typography>
              </Box>
            </Stack>

            {viewRequest.approvedByName && (
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">{t.approvedBy}</Typography>
                  <Typography variant="body1">{viewRequest.approvedByName}</Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="caption" color="text.secondary">{t.date}</Typography>
                  <Typography variant="body1">{formatDate(viewRequest.approvedDate)}</Typography>
                </Box>
              </Stack>
            )}

            {viewRequest.rejectionReason && (
              <Box>
                <Typography variant="caption" color="text.secondary">{t.rejectionReason}</Typography>
                <Typography variant="body1" color="error.main" sx={{ fontWeight: 600 }}>
                  {viewRequest.rejectionReason}
                </Typography>
              </Box>
            )}

            {viewRequest.notes && (
              <Box>
                <Typography variant="caption" color="text.secondary">{t.notes}</Typography>
                <Typography variant="body1">{viewRequest.notes}</Typography>
              </Box>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ background: theme.palette.action.hover }}>
                    <TableCell sx={{ fontWeight: 700 }}>{t.lineNumber}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.item}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.unit}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.requestedQty}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.approvedQty}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.priority}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.notes}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {viewRequest.details.map((d, idx) => (
                    <TableRow key={d.detailId}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {d.itemCode} - {isRTL ? d.itemNameAr : d.itemName}
                        </Typography>
                      </TableCell>
                      <TableCell>{isRTL ? d.unitNameAr : d.unitName}</TableCell>
                      <TableCell>{d.requestedQuantity.toFixed(3)}</TableCell>
                      <TableCell>{d.approvedQuantity != null ? d.approvedQuantity.toFixed(3) : '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={getPriorityLabel(d.priority)}
                          size="small"
                          color={d.priority === 'High' ? 'error' : d.priority === 'Medium' ? 'warning' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{d.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setViewOpen(false)} variant="outlined">
          {t.close}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ---- Reject Dialog ----
  const renderRejectDialog = () => (
    <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          background: 'linear-gradient(135deg, #e53e3e 0%, #c53030 100%)',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CancelIcon />
        {t.reject}
      </DialogTitle>
      <DialogContent sx={{ pt: '16px !important' }}>
        <TextField
          label={t.rejectReason}
          placeholder={t.rejectReasonPlaceholder}
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          multiline
          rows={3}
          fullWidth
          required
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => setRejectOpen(false)} color="inherit">
          {t.cancel}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleRejectConfirm}
          disabled={!rejectReason.trim()}
        >
          {t.reject}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ---- Main render ----
  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar
            sx={{
              width: 56,
              height: 56,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            <ShoppingCartIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              {t.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {stats.total} {isRTL ? 'طلب' : 'requests'}
            </Typography>
          </Box>
        </Stack>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadRequests} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            size="large"
            sx={{
              background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
              boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
              px: 4,
              py: 1.5,
              '&:hover': {
                background: 'linear-gradient(45deg, #5a6fd6 30%, #6a4296 90%)',
              },
            }}
          >
            {t.newRequest}
          </Button>
        </Box>
      </Stack>

      {/* Stats */}
      {renderStatsCards()}

      {/* Filter Bar */}
      {renderFilterBar()}

      {/* Tabs */}
      <Paper elevation={2} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', fontSize: '0.95rem' },
          }}
        >
          <Tab label={t.myRequests} />
          <Tab label={t.consolidatedView} />
          <Tab label={t.dailyPricing} />
          <Tab label={t.surplusStock} />
        </Tabs>
      </Paper>

      {/* Tab content */}
      {activeTab === 0 && renderRequestsTable()}
      {activeTab === 1 && renderConsolidatedView()}
      {activeTab === 2 && renderDailyPricing()}
      {activeTab === 3 && renderSurplusStock()}

      {/* Dialogs */}
      {renderDialog()}
      {renderViewDialog()}
      {renderRejectDialog()}
    </Box>
  );
};

export default BranchPurchaseRequestsPage2;
